import { defineComponent, defineAsyncComponent, ref, createApp, watch } from 'vue'
import { BlockModule } from '../common'
import { useBreakpoints } from '@vueuse/core'
import { tailwindBreakpoints } from '../tailwind'

new BlockModule({
    selector: '.block-product-cards-carousel',
    async setupBlock(el, onTeardown) {
        let isSwiperInit = false
        const initSwiper = async () => {
            const swiperContainer = el.querySelector('.swiper') as HTMLElement

            if (!swiperContainer) return

            const [{ Swiper }, { Scrollbar }] = await Promise.all([
                import('swiper'),
                import('swiper/modules'),
                // @ts-ignore
                import('swiper/css'),
                // @ts-ignore
                import('swiper/css/scrollbar'),
            ])

            const slides = Array.from(swiperContainer.querySelectorAll('.swiper-slide')) as HTMLElement[] || []

            if (slides.length <= 1) return

            const scrollBarEl = el.querySelector('.swiper-scrollbar') as HTMLElement

            const swiper = new Swiper(swiperContainer, {
                modules: [Scrollbar],
                slidesPerView: 'auto',
                spaceBetween: 20,
                scrollbar: {
                    el: scrollBarEl,
                    draggable: true,
                },
            })

            isSwiperInit = true

            const slideElements = Array.from(el.querySelectorAll('.swiper-slide'))
            if (slideElements.length === 0) return
            const lastCard = slideElements[slideElements.length - 1]

            const targetSpan = el.querySelector('.gradient-overlay')
            if (!targetSpan) return

            targetSpan.classList.add('opacity-100', 'translate-x-0')

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            targetSpan.classList.remove('opacity-100', 'translate-x-0')
                            targetSpan.classList.add('opacity-0', 'translate-x-full')
                        } else {
                            targetSpan.classList.remove('opacity-0', 'translate-x-full')
                            targetSpan.classList.add('opacity-100', 'translate-x-0')
                        }
                    })
                },
                {
                    threshold: 0.8,
                },
            )

            observer.observe(lastCard)

            onTeardown(() => {
                swiper.destroy()
                observer.disconnect()
            })
        }

        const productCardsCarouselBlock = el.querySelector('.product-cards-carousel-block')

        if (productCardsCarouselBlock) {
            const productCardsCarouselAppComponent = defineComponent({
                name: 'product-cards-carousel-block',
                components: {
                    Drawer: defineAsyncComponent(
                        () => import('@/components/ui/drawer/Drawer.vue')
                    ),
                    DrawerClose: defineAsyncComponent(
                        () => import('@/components/ui/dialog/DialogClose.vue'),
                    ),
                    DrawerContent: defineAsyncComponent(
                        () => import('@/components/ui/drawer/DrawerContent.vue'),
                    ),
                    DrawerDescription: defineAsyncComponent(
                        () => import('@/components/ui/drawer/DrawerDescription.vue'),
                    ),
                    DrawerFooter: defineAsyncComponent(
                        () => import('@/components/ui/drawer/DrawerFooter.vue'),
                    ),
                    DrawerTitle: defineAsyncComponent(
                        () => import('@/components/ui/drawer/DrawerTitle.vue'),
                    ),
                    DrawerTrigger: defineAsyncComponent(
                        () => import('@/components/ui/dialog/DialogTrigger.vue'),
                    ),
                },
                setup() {
                    const breakpoints = useBreakpoints(tailwindBreakpoints)
                    const mdAndGreater = breakpoints.greaterOrEqual('md')

                    watch(
                        mdAndGreater,
                        (val) => {
                            if (val && !isSwiperInit) {
                                initSwiper()
                            }
                        },
                        { immediate: true },
                    )

                    return {
                        mdAndGreater,
                    }
                },
            })

            const productCardsCarouselVueApp = createApp(productCardsCarouselAppComponent)
            productCardsCarouselVueApp.mount(productCardsCarouselBlock)

            onTeardown(() => {
                productCardsCarouselVueApp.unmount()
            })
        }
    },
})
